import { useEffect, useState } from 'react'
import { httpsCallable, getFunctions } from 'firebase/functions'
import _omit from 'lodash.omit'

import { useToast } from '../context/ToastContext'

/*
  Session -
    id,
    name,
    startDate,
    inputs,
    history,
    output
*/

const useApiHook = (inputEnv, encodedApiKey) => {
  const { showToast } = useToast()
  const execApi = httpsCallable(getFunctions(), 'apiProxy')
  const [env, setEnv] = useState(inputEnv)
  const [activeSessionId, setActiveSessionId] = useState()
  const [sessions, setSessions] = useState({})
  const [isApiCalling, setIsApiCalling] = useState(false)
  const [currentKey, setCurrentKey] = useState(encodedApiKey)

  useEffect(() => {
    setCurrentKey(encodedApiKey)
  }, [encodedApiKey])

  const callApi = async ({ method, body, headers = {}, query, route, saveSuccessPath }) => {
    if (isApiCalling) {
      return
    }
    setIsApiCalling(true)

    const replacedRoute = route.replace('{id}', sessions[activeSessionId]?.contact?.id)
    // Build request payload, include encodedApiKey if provided (customer use case)
    const payload = { method, body, query, headers, route: replacedRoute, env }
    if (currentKey) {
      payload.encodedApiKey = currentKey
    }
    const { data: { status, body: responseBody } } = await execApi(payload)

    // update history
    const updated = { ...sessions }
    updated[activeSessionId].history.push({
      request: { method, body, query, route: replacedRoute, env },
      response: { status, body: responseBody }
    })

    // update session if requested by saveSuccessPath
    if (status >= 200 && status < 300 && saveSuccessPath) {
      const existing = updated[activeSessionId][saveSuccessPath]
      // Merge with existing data for objects to preserve fields like id
      if (existing && typeof existing === 'object' && !Array.isArray(existing) && typeof responseBody === 'object' && !Array.isArray(responseBody)) {
        updated[activeSessionId][saveSuccessPath] = { ...existing, ...responseBody }
      } else {
        updated[activeSessionId][saveSuccessPath] = responseBody
      }
    }

    if (status >= 200 && status < 300) {
      showToast(`${route} ${method} Successful`, 'green')
    } else {
      showToast(`${route} ${method} Failed`, 'red')
    }

    setSessions(updated)
    setIsApiCalling(false)

    return { status, body: responseBody }
  }

  const createSession = ({ id, name }) => {
    setSessions({
      ...sessions,
      [id]: {
        id,
        name,
        startDate: new Date(),
        inputs: {},
        history: [],
        output: []
      }
    })
  }

  const selectSession = (id) => {
    setActiveSessionId(id)
  }

  return {
    sessions: Object.values(sessions).map(session => _omit(session, ['inputs', 'history', 'output'])),
    activeSession: sessions[activeSessionId],
    isApiCalling,
    env,
    setEnv,
    setApiKey: setCurrentKey,
    createSession,
    selectSession,
    callApi
  }
}

export default useApiHook
